// «Финансы» — логика экрана без Vue (решение Анны 02.11.2026): планы месяца, выполнение на сегодня,
// гипотеза по месяцу по распределению прошлого месяца. Расчёт — на сервере, здесь только подача.
import { LIGHT_LABELS, formatGoalValue, formatMoney } from '../workspace/workspaceLogic.mjs'

export { LIGHT_LABELS, formatGoalValue, formatMoney }

export const MONTHS = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь',
  'октябрь', 'ноябрь', 'декабрь']
const MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября',
  'октября', 'ноября', 'декабря']

function parseMonth(month) {
  const match = /^(\d{4})-(\d{2})$/.exec(String(month || ''))
  if (!match) return null
  const year = Number(match[1])
  const mon = Number(match[2])
  return mon >= 1 && mon <= 12 ? { year, mon } : null
}

export function currentMonth(now = new Date()) {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

export function monthTitle(month) {
  const p = parseMonth(month)
  if (!p) return ''
  const name = MONTHS[p.mon - 1]
  return `${name[0].toUpperCase()}${name.slice(1)} ${p.year}`
}

export function monthGenitive(month) {
  const p = parseMonth(month)
  return p ? MONTHS_GEN[p.mon - 1] : ''
}

export function shiftMonth(month, delta) {
  const p = parseMonth(month)
  if (!p) return month
  const index = p.year * 12 + (p.mon - 1) + delta
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`
}

export function isEmpty(value) {
  return value === null || value === undefined || Number.isNaN(value)
}

export function pctText(value) {
  if (isEmpty(value)) return '—'
  return `${String(Math.round(value * 10) / 10).replace('.', ',')} %`
}

// Ошибка загрузки — по-человечески, без устройства системы.
export function humanError(e) {
  const status = e?.response?.status
  const detail = String(e?.response?.data?.detail || '')
  if (status === 400 && detail) return detail
  if (status === 403) return 'раздел доступен руководителю логистики'
  if (status === 503 || /snapshot|not built|unreachable/i.test(detail)) {
    return 'данные ещё готовятся после обновления — через минуту нажмите «Обновить»'
  }
  if (!e?.response) return 'нет связи с сервером'
  return 'сервер не ответил, попробуйте ещё раз'
}

export function lightLabel(light) {
  return LIGHT_LABELS[light] || ''
}

export function planText(card) {
  if (!card) return ''
  if (card.direction === 'not_negative') return 'правило: не ниже 0'
  if (isEmpty(card.target)) return 'план не задан'
  const target = formatGoalValue(card.unit, card.target)
  return card.direction === 'lower' ? `план: не больше ${target}` : `план: ${target}`
}

export const METHOD_TEXT = {
  prev_month: 'по распределению прошлого месяца',
  pace: 'по темпу текущего месяца',
  fact: 'месяц закрыт — факт',
}

export function methodText(method) {
  return METHOD_TEXT[method] || ''
}

// Ширина полосы «факт к плану месяца» и метка «должно быть к сегодня» (0…100 %).
export function barModel(card) {
  if (!card || isEmpty(card.target) || !(card.target > 0) || isEmpty(card.value)) {
    return { fact: 0, mark: null, forecast: null }
  }
  const clamp = (v) => Math.max(0, Math.min(100, v))
  return {
    fact: clamp((card.value / card.target) * 100),
    mark: isEmpty(card.plan_to_date) ? null : clamp((card.plan_to_date / card.target) * 100),
    forecast: isEmpty(card.forecast) ? null : clamp((card.forecast / card.target) * 100),
  }
}

// Строка нижнего блока «На сегодня»: как идём к графику плана.
export function todayStatus(row) {
  if (!row || isEmpty(row.value)) return { tone: 'grey', text: 'нет данных' }
  if (isEmpty(row.target)) return { tone: 'grey', text: 'план не задан' }
  const lower = row.direction === 'lower'
  if (isEmpty(row.plan_to_date)) {
    // Показатели «на дату» (маржа, доля): сравниваем текущее значение с планом.
    const ok = lower ? row.value <= row.target : row.value >= row.target
    return ok ? { tone: 'green', text: 'в плане' } : { tone: 'red', text: lower ? 'выше плана' : 'ниже плана' }
  }
  const diff = row.value - row.plan_to_date
  if (Math.abs(diff) < 0.5) return { tone: 'green', text: 'в графике' }
  if (lower) {
    return diff <= 0
      ? { tone: 'green', text: 'в бюджете' }
      : { tone: row.to_date_pct <= 110 ? 'yellow' : 'red', text: `перерасход ${formatGoalValue(row.unit, diff)}` }
  }
  if (diff >= 0) return { tone: 'green', text: `опережаем на ${formatGoalValue(row.unit, diff)}` }
  return {
    tone: row.to_date_pct >= 90 ? 'yellow' : 'red',
    text: `отстаём на ${formatGoalValue(row.unit, -diff)}`,
  }
}

export function sourceText(plan, month) {
  if (!plan) return ''
  if (plan.unavailable) return 'Планы временно недоступны — показаны значения по умолчанию'
  if (plan.source === 'month') return `План ${monthGenitive(month)} задан`
  if (plan.source === 'inherited' && plan.source_month) {
    return `Действует план ${monthGenitive(plan.source_month)} — сохраните, чтобы задать свой`
  }
  return 'План по умолчанию — задайте план месяца'
}

// Ввод плана: «7 500 000», «7,5 млн», «19,5», пусто — «план не задан».
export function parsePlanInput(text, unit) {
  const raw = String(text ?? '').trim().toLowerCase()
  if (!raw) return { value: null }
  let multiplier = 1
  let body = raw.replace(/₽|руб\.?|%/g, '').trim()
  if (/(млн|m)$/.test(body)) { multiplier = 1_000_000; body = body.replace(/(млн|m)$/, '').trim() }
  else if (/(тыс\.?|к|k)$/.test(body)) { multiplier = 1_000; body = body.replace(/(тыс\.?|к|k)$/, '').trim() }
  const number = Number(body.replace(/[\s ]/g, '').replace(',', '.'))
  if (!Number.isFinite(number)) return { error: 'Нужно число' }
  const value = Math.round(number * multiplier * 100) / 100
  if (value < 0) return { error: 'Не меньше нуля' }
  if (unit === 'pct' && value > 100) return { error: 'Не больше 100 %' }
  if (unit === 'count' && !Number.isInteger(value)) return { error: 'Нужно целое число' }
  return { value }
}

export function planInputText(value, unit) {
  if (isEmpty(value)) return ''
  if (unit === 'rub') return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(value)
  return String(value).replace('.', ',')
}

// Что отправить на сервер: только изменённые показатели; ошибки — до запроса.
export function buildPlanPayload(month, goals, drafts, comment) {
  const errors = {}
  const targets = {}
  for (const goal of goals || []) {
    if (!(goal.key in (drafts || {}))) continue
    const parsed = parsePlanInput(drafts[goal.key], goal.unit)
    if (parsed.error) { errors[goal.key] = parsed.error; continue }
    const before = isEmpty(goal.target) ? null : goal.target
    if (parsed.value === before) continue
    if (parsed.value !== null && before !== null && Math.abs(parsed.value - before) < 0.005) continue
    targets[goal.key] = parsed.value
  }
  if (Object.keys(errors).length) return { errors }
  if (!Object.keys(targets).length) return { error: 'План не изменился' }
  const reason = String(comment || '').trim()
  if (reason.length < 3) return { error: 'Напишите, почему меняете план — это попадёт в историю' }
  return { payload: { month, targets, comment: reason } }
}

// Кривые «накоплено к дню»: SVG-пути текущего и прошлого месяца + линия плана.
export function chartModel(chart, { width = 640, height = 220, pad = 18, plan = null } = {}) {
  const points = (chart && chart.points) || []
  if (!points.length) return null
  const values = []
  for (const p of points) {
    if (!isEmpty(p.current)) values.push(p.current)
    if (!isEmpty(p.previous)) values.push(p.previous)
  }
  if (!isEmpty(plan) && plan > 0) values.push(plan)
  const max = Math.max(1, ...values.map((v) => Math.abs(v)))
  const min = Math.min(0, ...values)
  const days = points.length
  const x = (day) => pad + ((day - 1) / Math.max(1, days - 1)) * (width - pad * 2)
  const y = (v) => height - pad - ((v - min) / (max - min || 1)) * (height - pad * 2)
  const path = (key) => {
    let d = ''
    for (const p of points) {
      if (isEmpty(p[key])) continue
      d += `${d ? 'L' : 'M'}${x(p.day).toFixed(1)},${y(p[key]).toFixed(1)}`
    }
    return d
  }
  const current = path('current')
  const lastCurrent = [...points].reverse().find((p) => !isEmpty(p.current))
  const area = current && lastCurrent
    ? `${current}L${x(lastCurrent.day).toFixed(1)},${y(min).toFixed(1)}L${x(points[0].day).toFixed(1)},${y(min).toFixed(1)}Z`
    : ''
  return {
    width,
    height,
    current,
    previous: path('previous'),
    area,
    planY: !isEmpty(plan) && plan > 0 ? y(plan) : null,
    todayX: lastCurrent ? x(lastCurrent.day) : null,
    todayY: lastCurrent ? y(lastCurrent.current) : null,
    max,
  }
}

export function historyText(item, unit) {
  if (!item) return ''
  const fmt = (v) => (isEmpty(v) ? 'не задан' : formatGoalValue(unit || 'rub', v))
  return `${fmt(item.old)} → ${fmt(item.new)}`
}

export function formatDateTime(value) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// Плавный счётчик: значение на шаге анимации (ease-out cubic).
export function tween(from, to, progress) {
  const t = Math.max(0, Math.min(1, progress))
  const eased = 1 - (1 - t) ** 3
  return from + (to - from) * eased
}
